import React, { Component } from 'react';
import { connect } from 'react-redux';
import { BrowserRouter, Route, Switch } from "react-router-dom";
import indexRoutes from "routes/index.jsx";
import OfflineToggle from "./components/OfflineToggle/OfflineToggle.jsx";
import { runBackgroundOfflineSync } from "./helpers/offlineSync";

class App extends Component {
    state = {
        deferredPrompt: null,
        showInstallButton: false,
        showInstallPopup: false,
        isAppInstalled: false,
        installationStatus: null,
        progress: 0,
        showProgressBar: false,
        isOnline: navigator.onLine
    };

    progressInterval = null;

    componentDidMount() {
        this.setupPWA();
        this.checkInstallStatus();
        this.setupOnlineOfflineHandlers();
    }

    componentWillUnmount() {
        this.cleanupEvents();
        this.clearProgressInterval();
        this.cleanupOnlineOfflineHandlers();
    }

    setupPWA = () => {
        window.addEventListener('beforeinstallprompt', (e) => {
            e.preventDefault();
            const dismissed = localStorage.getItem('installPopupDismissed') === 'true';
            this.setState({
                deferredPrompt: e,
                showInstallButton: true,
                showInstallPopup: !dismissed
            });
        });

        window.addEventListener('appinstalled', () => {
            this.setState({
                isAppInstalled: true,
                showInstallButton: false,
                showInstallPopup: false,
                installationStatus: 'success',
                showProgressBar: false
            });
            this.clearProgressInterval();
        });
    };

    handlePopupInstall = () => {
        this.setState({ showInstallPopup: false });
        this.handleInstallClick();
    };

    handlePopupDismiss = () => {
        localStorage.setItem('installPopupDismissed', 'true');
        this.setState({ showInstallPopup: false });
    };

    setupOnlineOfflineHandlers = () => {
        window.addEventListener('online', this.handleOnlineStatus);
        window.addEventListener('offline', this.handleOfflineStatus);
    };

    cleanupOnlineOfflineHandlers = () => {
        window.removeEventListener('online', this.handleOnlineStatus);
        window.removeEventListener('offline', this.handleOfflineStatus);
    };

    handleOnlineStatus = () => {
        this.setState({ isOnline: true });
        // Reconnexion : re-synchroniser les données hors-ligne en arrière-plan.
        if (this.props.dispatch) {
            runBackgroundOfflineSync(this.props.dispatch);
        }
    };

    handleOfflineStatus = () => {
        this.setState({ isOnline: false });
    };

    checkInstallStatus = () => {
        const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
        if (isStandalone) {
            this.setState({ isAppInstalled: true });
        }
    };

    cleanupEvents = () => {
    };

    clearProgressInterval = () => {
        if (this.progressInterval) {
            clearInterval(this.progressInterval);
            this.progressInterval = null;
        }
    };

    handleInstallClick = async () => {
        const { deferredPrompt } = this.state;

        this.setState({
            installationStatus: 'pending',
            showProgressBar: true,
            progress: 0
        });

        this.startProgressSimulation();

        try {
            if (deferredPrompt) {
                const result = await deferredPrompt.prompt();
                const choiceResult = await result.userChoice;

                if (choiceResult.outcome === 'accepted') {
                } else {
                    this.setState({
                        installationStatus: 'dismissed',
                        showProgressBar: false
                    });
                    this.clearProgressInterval();
                }
            } else {
                this.showManualInstallInstructions();
            }
        } catch (error) {
            this.setState({
                installationStatus: 'error',
                showProgressBar: false
            });
            this.clearProgressInterval();
            this.showManualInstallInstructions();
        }
    };

    showManualInstallInstructions = () => {
        const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
        const instructions = isIOS
            ? "1. Appuyez sur l'icône de partage\n2. Sélectionnez 'Sur l'écran d'accueil'"
            : "1. Ouvrez le menu du navigateur (⋮)\n2. Sélectionnez 'Installer l'application'";

        alert(`Pour installer cette application:\n\n${instructions}`);
    };

    startProgressSimulation = () => {
        this.setState({
            showProgressBar: true,
            progress: 0,
            installationStatus: 'pending'
        });

        this.progressInterval = setInterval(() => {
            this.setState(prevState => {
                const newProgress = prevState.progress + 10;
                if (newProgress >= 100) {
                    clearInterval(this.progressInterval);
                    return {
                        progress: 100,
                        installationStatus: 'success',
                        isAppInstalled: true
                    };
                }
                return { progress: newProgress };
            });
        }, 300);
    };

    renderProgressBar() {
        const { progress } = this.state;

        return (
            <div style={{
                position: 'fixed',
                bottom: '80px',
                right: '20px',
                width: '250px',
                backgroundColor: '#f1f1f1',
                borderRadius: '4px',
                boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
                zIndex: 1000,
                overflow: 'hidden'
            }}>
                <div style={{
                    width: `${progress}%`,
                    height: '20px',
                    backgroundColor: progress === 100 ? '#4CAF50' : '#2196F3',
                    transition: 'width 0.3s ease, background-color 0.3s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '12px',
                    fontWeight: 'bold'
                }}>
                    {progress}%
                </div>
            </div>
        );
    }

    renderInstallPopup() {
        const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
        const supportText = isMobile
            ? "votre téléphone ou tablette"
            : "votre ordinateur";

        return (
            <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                backgroundColor: 'rgba(0,0,0,0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 2000
            }}>
                <div style={{
                    backgroundColor: '#fff',
                    borderRadius: '10px',
                    padding: '30px',
                    maxWidth: '420px',
                    width: '90%',
                    boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
                    textAlign: 'center'
                }}>
                    <div style={{
                        width: '70px',
                        height: '70px',
                        margin: '0 auto 20px',
                        borderRadius: '50%',
                        backgroundColor: '#e8f4fd',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        <svg style={{ width: '36px', height: '36px' }} viewBox="0 0 24 24" fill="#1f8ef1">
                            <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
                        </svg>
                    </div>
                    <h3 style={{ marginTop: 0, marginBottom: '12px', color: '#2c3e50' }}>
                        Installer APURMED
                    </h3>
                    <p style={{ color: '#6c757d', lineHeight: '1.6', marginBottom: '25px' }}>
                        Installez APURMED sur {supportText} pour un accès rapide,
                        un démarrage en un clic et une utilisation même hors connexion.
                    </p>
                    <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                        <button
                            onClick={this.handlePopupDismiss}
                            style={{
                                flex: 1,
                                padding: '12px',
                                borderRadius: '6px',
                                border: '1px solid #ced4da',
                                backgroundColor: '#fff',
                                color: '#6c757d',
                                cursor: 'pointer',
                                fontSize: '15px'
                            }}
                        >
                            Plus tard
                        </button>
                        <button
                            onClick={this.handlePopupInstall}
                            style={{
                                flex: 1,
                                padding: '12px',
                                borderRadius: '6px',
                                border: 'none',
                                backgroundColor: '#1f8ef1',
                                color: '#fff',
                                cursor: 'pointer',
                                fontSize: '15px',
                                fontWeight: 'bold'
                            }}
                        >
                            Installer
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    renderInstallButton() {
        const { installationStatus, showProgressBar } = this.state;
        const isInstalling = installationStatus === 'pending';

        return (
            <div style={{
                position: 'fixed',
                bottom: '20px',
                right: '20px',
                zIndex: 1001,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-end'
            }}>
                {showProgressBar && this.renderProgressBar()}

                <button
                    onClick={!isInstalling ? this.handleInstallClick : null}
                    disabled={isInstalling}
                    style={{
                        backgroundColor: isInstalling ? '#FFA500' : '#4CAF50',
                        color: 'white',
                        padding: '12px 24px',
                        borderRadius: '4px',
                        border: 'none',
                        cursor: isInstalling ? 'wait' : 'pointer',
                        boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
                        display: 'flex',
                        alignItems: 'center',
                        fontSize: '16px',
                        transition: 'all 0.3s ease'
                    }}
                >
                    {isInstalling ? (
                        <>
                            <svg
                                style={{
                                    marginRight: '10px',
                                    width: '20px',
                                    height: '20px',
                                    animation: 'spin 1s linear infinite'
                                }}
                                viewBox="0 0 24 24"
                                fill="white"
                            >
                                <path d="M12,4V2A10,10 0 0,0 2,12H4A8,8 0 0,1 12,4Z"/>
                            </svg>
                            Installation...
                        </>
                    ) : (
                        <>
                            <svg
                                style={{ marginRight: '10px', width: '20px', height: '20px' }}
                                viewBox="0 0 24 24"
                                fill="white"
                            >
                                <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
                            </svg>
                            Installer
                        </>
                    )}
                </button>
            </div>
        );
    }

    renderOfflineBanner() {
        return (
            <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100%',
                backgroundColor: '#6c757d',
                color: 'white',
                textAlign: 'center',
                padding: '6px 12px',
                fontSize: '13px',
                fontWeight: '500',
                zIndex: 2000,
                boxShadow: '0 2px 6px rgba(0,0,0,0.2)'
            }}>
                <i className="fa fa-wifi" style={{ marginRight: '8px', opacity: 0.7 }} />
                Mode hors ligne — vous consultez les données enregistrées sur cet appareil
            </div>
        );
    }

    render() {
        const { showInstallButton, showInstallPopup, isAppInstalled, isOnline } = this.state;

        return (
            <BrowserRouter>
                <div className="wrapper">
                    {!isOnline && this.renderOfflineBanner()}
                    <div className="offline-toggle-container">
                        <OfflineToggle />
                    </div>
                    <Switch>
                        {indexRoutes.map((prop, key) => {
                            return (
                                <Route
                                    path={prop.path}
                                    exact={prop.exact}
                                    component={prop.component}
                                    key={key}
                                />
                            );
                        })}
                    </Switch>
                    {showInstallButton && !isAppInstalled && this.renderInstallButton()}
                    {showInstallPopup && !isAppInstalled && this.renderInstallPopup()}
                </div>
            </BrowserRouter>
        );
    }
}

export default connect()(App);
